import React, {
  FC,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import CloseIcon from '@material-ui/icons/Close';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import { selectLocale } from 'models/localization';
import { fetchParticipantList } from 'models/participants';
import useEnableHotjarTreatment from 'hooks/useEnableHotjarTreatment';
import { nameByLocale } from 'hooks/useNameByLocale';
import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import {
  selectAllUserActivityLog,
  selectFetchingUserActivityLog,
} from 'models/userActivityLog';
import UserActivityLogItem from './UserActivityLogItem';
import UserActivityLogObserver from './UserActivityLogObserver';
import NoActivityIndicator from './components/NoActivityIndicator';
import LoadingIndicator from './components/LoadingIndicator';

const UserActivityLogContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const UserActivityLogHeader = styled.div`
  width: 100%;
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
`;

const FilterContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;

const DropDownFilterContainer = styled.div`
  flex: 1;
  max-width: 200px;
`;

const SelectedFilterContainer = styled.div`
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
`;

const UserActivityLogList = styled.div<{ $extraHeight: boolean }>`
  width: 100%;
  height: calc(100% - ${({ $extraHeight }) => ($extraHeight ? 102 : 66)}px);
  overflow: hidden;
  overflow-y: scroll;
`;

const StyledChip = styled(Chip)`
  border-radius: 8px;

  &.MuiChip-root {
    color: white;
    background-color: #5C8DF3;
    margin: 0 2px;
  }

  .MuiChip-iconColorPrimary {
    color: white;
  }
`;

const RemoveFilterIcon = styled(CloseIcon)`
  color: white;
  padding: 4px;
`;

const ChipContainer = styled.div`
  flex: 6;
`;

const ResetButton = styled(Button)`
  &.MuiButton-outlined {
    border: 1px solid #5C8DF3;
  }

  &.MuiButton-root {
    color: #5C8DF3;
  }
`;

interface UserActivityLogProps {
  boothId: string;
  currentUserAuthToken: string;
  isOpened: boolean;
}

const triggerHotjarSurvey = () => {
  if ((window as any).hj) {
    (window as any).hj('trigger', 'trigger_exhibitor_panel_survey');
  } else {
    setTimeout(() => {
      triggerHotjarSurvey();
    }, 1000);
  }
};

const UserActivityLog: FC<UserActivityLogProps> = ({
  boothId,
  currentUserAuthToken,
  isOpened,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { chatroomConfig } = useSelector((s) => s.eventConfigs);
  const chatEnabled = useMemo(() => !isChatroomDisabled(chatroomConfig), [chatroomConfig]);
  const [filter, setFilter] = useState(null);
  const [activityLogTarget, setActivityLogTarget] = useState<{ type?: string, value?: string, attr?: any }>(null);
  const locale = useSelector(selectLocale);
  const enabledHotJar = useEnableHotjarTreatment();

  const userActivityLog = useSelector(selectAllUserActivityLog);
  const fetchingUserActivityLog = useSelector(selectFetchingUserActivityLog);
  const eventList = useMemo(() => (
    [
      { label: t('activity_log.enter_booth_message', 'Entered the booth'), value: 'Enter' },
      { label: t('activity_log.file_view_message', 'Viewed file(s)'), value: 'File_View' },
      { label: t('activity_log.meetup_attempt', 'Tried to schedule Meetup'), value: 'Meetup' },
      { label: t('activity_log.scheduled_meetup', 'Scheduled Meetup'), value: 'Submitted_Meetup' },
    ]
  ), [t]);

  const fetchParticipant = () => {
    dispatch(fetchParticipantList()).then((result: any) => {
      if (result?.payload?.hasNextPage) {
        fetchParticipant();
      }
    });
  };

  useEffect(() => {
    fetchParticipant();
  }, []);

  useEffect(() => {
    if (enabledHotJar) {
      triggerHotjarSurvey();
    }
  }, [enabledHotJar]);

  const renderResetButton = useMemo(() => {
    if (activityLogTarget || filter) {
      return (
        <ResetButton
          onClick={() => {
            setActivityLogTarget(null);
            setFilter(null);
          }}
          variant="outlined"
        >
          {t('activity_log.clear_filters', 'Clear Filter(s)')}
        </ResetButton>
      );
    }
    return null;
  }, [filter, activityLogTarget, setActivityLogTarget, t]);

  const renderActivityLog = useMemo(() => {
    if (userActivityLog.length) {
      return (
        userActivityLog.map((log: any) => (
          <UserActivityLogItem
            key={log.id}
            log={log}
            setFilter={setFilter}
            setActivityLogTarget={setActivityLogTarget}
            locale={locale}
            activityLogTarget={activityLogTarget}
            filter={filter}
            chatEnabled={chatEnabled}
          />
        ))
      );
    }
    if (!fetchingUserActivityLog) {
      return (
        <NoActivityIndicator />
      );
    }
    return (
      <LoadingIndicator />
    );
  }, [
    fetchingUserActivityLog,
    userActivityLog,
    setActivityLogTarget,
    filter,
    activityLogTarget,
    locale,
  ]);

  const handleDeleteFilter = useCallback(() => {
    setFilter(null);
  }, [setFilter]);

  const handleDeleteActivityTarget = useCallback(() => {
    setActivityLogTarget(null);
  }, [setActivityLogTarget]);

  const renderChipLabel = useMemo(() => {
    if (activityLogTarget) {
      const { type, value } = activityLogTarget;
      if (type === 'event_info') {
        return `${t('activity_log.file_view_message', 'Viewed file(s)')} ${value}`;
      }
      if (type === 'user_token') {
        const { attr: { userFirstName, userLastName } } = activityLogTarget;
        return nameByLocale(userFirstName, userLastName, locale);
      }
    }
    return null;
  }, [activityLogTarget, locale, t]);

  return (
    <UserActivityLogContainer>
      <UserActivityLogHeader>
        <FilterContainer>
          <DropDownFilterContainer>
            {isOpened && (
              <Select
                closeMenuOnSelect
                options={eventList}
                onChange={(value) => setFilter(value)}
                value={filter}
                placeholder={t('activity_log.all_activity', 'All activities')}
                displayEmpty
                isDisabled={filter}
              />
            )}
          </DropDownFilterContainer>
        </FilterContainer>
      </UserActivityLogHeader>
      <SelectedFilterContainer>
        <ChipContainer>
          {
            activityLogTarget && (
              <StyledChip
                label={renderChipLabel}
                onDelete={handleDeleteActivityTarget}
                deleteIcon={<RemoveFilterIcon />}
              />
            )
          }
          {
            filter && (
              <StyledChip
                label={filter.label}
                onDelete={handleDeleteFilter}
                deleteIcon={<RemoveFilterIcon />}
              />
            )
          }
        </ChipContainer>
        {renderResetButton}
      </SelectedFilterContainer>
      <UserActivityLogObserver boothId={boothId} filter={filter} activityLogTarget={activityLogTarget} />
      <UserActivityLogList
        $extraHeight={activityLogTarget || filter}
      >
        {renderActivityLog}
      </UserActivityLogList>
    </UserActivityLogContainer>
  );
};

export default UserActivityLog;
